/* eslint-disable react-hooks/exhaustive-deps */

import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useLazyOptimizelyExperiment } from '@zoopla/optimizely';
import { Dialog } from '@zoopla-eng/zds-components/dialog';
import { Box } from '@zoopla-eng/zds-components/box';
import triggerTracking from '@blueprint/tracking/dist/Tracking';
import { useDispatch } from 'react-redux';

import {
    AuthModalMode,
    AuthModalProps,
    AuthModalStepKeys,
    AuthModalStepsConfig
} from './AuthModal.types';
import { trackQuestionClose } from './AuthModal.tracking';
import { setLoggedIn, useUser } from '@zoopla/redux-reducer-user';
import Preferences from './Preferences';
import { RegTabs } from './RegTabs/RegTabs';
import { User } from '../../graphql.generated';
import { isNewUser } from './utils';
import {
    QuickAccess,
    eventCategory as quickAccessCategory
} from './section/QuickAccess';

const signInModalHeadingTn = 'Save properties';

const LazyYourSituationQuestion = dynamic(
    () => import('./YourSituationQuestion'),
    {
        ssr: false
    }
);

const getInitialMode = (initialMode: AuthModalProps['initialMode']) =>
    initialMode === 'quick-access'
        ? AuthModalStepKeys.QUICK_ACCESS
        : AuthModalStepKeys.FORM_TABS;

export const AuthModal: FC<PropsWithChildren<AuthModalProps>> = (props) => {
    const [isSocial, setIsSocial] = useState(false);
    const [isLoadingFlag, isFlagEnabled] = useLazyOptimizelyExperiment(
        'crme-2002-your-situation-reg-experiment'
    );
    const [currentStep, setCurrentStep] = useState<AuthModalStepKeys>(
        getInitialMode(props.initialMode)
    );
    const overrideInitialMode = useRef<AuthModalMode>();
    const { registered } = useUser();

    const {
        heading,
        isShowing,
        close,
        actionAfterAuth,
        resetActionAfterAuth = () => null
    } = props;

    const reduxDispatch = useDispatch();

    const socialAuthSignIn = async () => {
        reduxDispatch(setLoggedIn(true));
        await actionAfterAuth?.action();
        resetActionAfterAuth();
    };

    const socialAuthSignInAndClose = async () => {
        socialAuthSignIn();
        close();
    };

    const socialAuth = (user?: User) => {
        setIsSocial(true);
        if (isNewUser({ registered }, user?.creationDate)) {
            localStorage.setItem('new_register_user', 'true');

            setCurrentStep(AuthModalStepKeys.PREFERENCES);
            return;
        }

        socialAuthSignInAndClose();
    };

    // This runs when the user clicks the close button, outside the modal or uses the Escape key (not on submit)
    const toggleClosed = () => {
        const isSecondStep =
            currentStep === AuthModalStepKeys.PREFERENCES ||
            currentStep === AuthModalStepKeys.YOUR_SITUATION;
        // Email preference form is step 2 and Your situation question is step 3
        const stepNumber = isFlagEnabled === 'enabled' && isSocial ? 3 : 2;
        const secondStepEventLabel = `${actionAfterAuth?.eventLabel}_step_${stepNumber}`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory: steps[currentStep].trackingCategory || '',
            eventAction: 'form closed',
            eventLabel: isSecondStep
                ? secondStepEventLabel
                : actionAfterAuth?.eventLabel
        });

        if (!isLoadingFlag && isFlagEnabled === 'enabled') {
            if (currentStep === AuthModalStepKeys.PREFERENCES) {
                setCurrentStep(AuthModalStepKeys.YOUR_SITUATION);
                return;
            }

            if (currentStep === AuthModalStepKeys.YOUR_SITUATION) {
                resetActionAfterAuth();
                socialAuthSignInAndClose();
                return;
            }
        }

        currentStep === AuthModalStepKeys.PREFERENCES
            ? socialAuthSignIn()
            : resetActionAfterAuth();

        close();
    };

    const changeToOtherMode = (newMode: AuthModalMode) => {
        overrideInitialMode.current = newMode;
        setCurrentStep(AuthModalStepKeys.FORM_TABS);
    };

    const onPreferenceFormClose = () => {
        if (!isLoadingFlag && isFlagEnabled === 'enabled') {
            setCurrentStep(AuthModalStepKeys.YOUR_SITUATION);
            return;
        }

        socialAuthSignInAndClose();
    };

    const onSituationQuestionClose = (_goal = '', lastQuestion = '') => {
        trackQuestionClose(lastQuestion, actionAfterAuth?.segment?.page || '');
        toggleClosed();
    };

    const registration = (
        <RegTabs
            {...props}
            initialMode={overrideInitialMode.current || props.initialMode}
            onSocialAuth={socialAuth}
            setCurrentStep={setCurrentStep}
        />
    );

    const steps: AuthModalStepsConfig = {
        formTabs: {
            title: heading || signInModalHeadingTn,
            component: registration,
            trackingCategory:
                props.initialMode === 'sign-in' ? 'signin' : 'registration'
        },
        preferences: {
            component: (
                <Preferences
                    close={onPreferenceFormClose}
                    actionAfterAuth={actionAfterAuth}
                />
            ),
            trackingCategory: 'registration'
        },
        quickAccess: {
            title: heading || signInModalHeadingTn,
            component: (
                <QuickAccess
                    selectMode={changeToOtherMode}
                    actionAfterAuth={actionAfterAuth}
                    close={close}
                    introText="Follow a property's estimated price over time and get notified when it changes."
                    onSocialAuth={socialAuth}
                />
            ),
            trackingCategory: quickAccessCategory
        },
        yourSituation: {
            title: 'Customise your experience',
            component: (
                <LazyYourSituationQuestion
                    page={actionAfterAuth?.segment?.page || 'unknown'}
                    onClose={onSituationQuestionClose}
                />
            ),
            trackingCategory: 'registration'
        }
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            toggleClosed();
        }
    };

    useEffect(() => {
        if (isShowing) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: steps[currentStep].trackingCategory || '',
                eventAction: 'form shown',
                eventLabel: actionAfterAuth?.eventLabel
            });
        } else {
            setCurrentStep(getInitialMode(props.initialMode));
        }
    }, [isShowing, props.initialMode]);

    return (
        <Dialog
            isOpen={isShowing}
            onOpenChange={onOpenChange}
            title={steps[currentStep].title}
            label={steps[currentStep].title || ''}
        >
            <Box pr="$S300" pl="$S100">
                {steps[currentStep].component}
            </Box>
        </Dialog>
    );
};

export default AuthModal;
